import type { LucideIcon } from 'lucide-react';
import { handleRovingKeyDown, rovingIndexFor } from '@/lib/roving-focus.js';

interface SegmentedControlOption<T extends string | number> {
  value: T;
  label: string;
  icon?: LucideIcon;
}

interface SegmentedControlProps<T extends string | number> {
  options: SegmentedControlOption<T>[];
  /** `null` is "not answered yet" — `rovingIndexFor` still keeps the group to one tab stop. */
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
}

export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: SegmentedControlProps<T>) {
  const values = options.map((o) => o.value);
  const rovingIndex = rovingIndexFor(values, value);

  return (
    <div className="sm-control sm-segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt, index) => {
        const Icon = opt.icon;
        const selected = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="radio"
            className="sm-type-control sm-center sm-tabular sm-seg-tab"
            aria-checked={selected}
            tabIndex={index === rovingIndex ? 0 : -1}
            onKeyDown={(e) => handleRovingKeyDown(e, values, index, onChange)}
            onClick={() => onChange(opt.value)}
          >
            {Icon !== undefined && <Icon className="sm-seg-icon" size={14} aria-hidden="true" />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
